import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Grid,
  InputAdornment,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
} from '@mui/material';
import {
  Search as SearchIcon,
  Key as KeyIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import rolesService from '../../../services/rolesService';
import PageHeader from '../../../components/PageHeader';
import StatsCard from '../../../components/StatsCard';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";
const SECONDARY = "#5A6D8B";

export default function Permissions() {
  const [permissions, setPermissions] = useState({});
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [expanded, setExpanded] = useState({});

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    modules: 0,
    avgPerModule: 0,
  });

  useEffect(() => {
    loadPermissions();
    loadModules();
  }, []);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const response = await rolesService.getPermissions({
        search,
        module: selectedModule || undefined,
        group_by_module: true,
      });
      if (response.success) {
        const grouped = {};
        if (Array.isArray(response.permissions)) {
          response.permissions.forEach((perm) => {
            const module = perm.module || 'Other';
            if (!grouped[module]) {
              grouped[module] = [];
            }
            grouped[module].push(perm);
          });
        } else {
          Object.assign(grouped, response.permissions);
        }
        setPermissions(grouped);
        calculateStats(grouped);

        // Expand all by default
        const initialExpanded = {};
        Object.keys(grouped).forEach((module) => {
          initialExpanded[module] = true;
        });
        setExpanded(initialExpanded);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      const response = await rolesService.getModules();
      if (response.success) {
        setModules(response.modules || []);
      }
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  };

  const calculateStats = (permissionsData) => {
    const totalPermissions = Object.values(permissionsData).reduce((sum, perms) => sum + perms.length, 0);
    const moduleCount = Object.keys(permissionsData).length;

    setStats({
      total: totalPermissions,
      modules: moduleCount,
      avgPerModule: moduleCount > 0 ? Math.round(totalPermissions / moduleCount) : 0,
    });
  };

  const handleToggleExpand = (module) => {
    setExpanded({
      ...expanded,
      [module]: !expanded[module],
    });
  };

  const getModuleIcon = (module) => {
    const iconMap = {
      users: '👥',
      jobs: '💼',
      candidates: '👤',
      applications: '📝',
      assessments: '📋',
      roles: '🔐',
      dashboard: '📊',
      reports: '📈',
      settings: '⚙️',
    };
    return iconMap[module] || '📦';
  };

  const getModuleColor = (module) => {
    const colorMap = {
      users: '#2196f3',
      jobs: SUCCESS,
      candidates: '#ff9800',
      applications: '#9c27b0',
      assessments: '#f44336',
      roles: '#673ab7',
      dashboard: '#00bcd4',
      reports: '#ff5722',
      settings: '#795548',
    };
    return colorMap[module] || SECONDARY;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Permissions Overview"
        subtitle="View all system permissions grouped by module"
        icon={KeyIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Security' },
          { label: 'Permissions' }
        ]}
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            title="Total Permissions"
            value={stats.total}
            icon={KeyIcon}
            color={PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            title="Modules"
            value={stats.modules}
            icon={CategoryIcon}
            color={SUCCESS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            title="Avg Per Module"
            value={stats.avgPerModule}
            icon={KeyIcon}
            color="#2196f3"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search permissions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadPermissions()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Module</InputLabel>
              <Select
                value={selectedModule}
                onChange={(e) => {
                  setSelectedModule(e.target.value);
                  setTimeout(loadPermissions, 100);
                }}
                label="Filter by Module"
                sx={{
                  bgcolor: '#fff',
                }}
              >
                <MenuItem value="">All Modules</MenuItem>
                {modules.map((module) => (
                  <MenuItem key={module} value={module}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{getModuleIcon(module)}</span>
                      <span style={{ textTransform: 'capitalize' }}>{module}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Permissions List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: PRIMARY }} size={60} />
        </Box>
      ) : Object.keys(permissions).length === 0 ? (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 2 }}>
          <KeyIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
          <Typography color="text.secondary">No permissions found</Typography>
        </Card>
      ) : (
        <Box>
          {Object.entries(permissions).map(([module, modulePermissions]) => (
            <Accordion
              key={module}
              expanded={expanded[module]}
              onChange={() => handleToggleExpand(module)}
              sx={{
                mb: 2,
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: '#f5f5f5',
                  '&:hover': { bgcolor: '#eeeeee' },
                  borderLeft: `4px solid ${getModuleColor(module)}`,
                  borderRadius: '8px 8px 0 0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Typography sx={{ fontSize: 24 }}>{getModuleIcon(module)}</Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      {module}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {modulePermissions.length} permissions in this module
                    </Typography>
                  </Box>
                  <Chip
                    label={modulePermissions.length}
                    size="small"
                    sx={{
                      bgcolor: getModuleColor(module),
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>Permission Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>Display Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: PRIMARY }} align="center">
                          Used in Roles
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modulePermissions.map((permission) => (
                        <TableRow
                          key={permission.id}
                          sx={{
                            '&:hover': { bgcolor: '#f8f9fa' },
                            '&:nth-of-type(even)': { bgcolor: '#fdfdfd' },
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#666' }}
                            >
                              {permission.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {permission.display_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {permission.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={permission.roles?.length || 0}
                              size="small"
                              sx={{
                                bgcolor: `${PRIMARY}15`,
                                color: PRIMARY,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Container>
  );
}
